"use client";

import { useCallback, useEffect, useState } from "react";
import ConvertKitForm from "convertkit-react";
import { usePathname } from "next/navigation";
import CoreImage from "next/image";
import { useLenis } from "lenis/react";

import { getAssetUrl } from "@/lib/cms";

import { LayoutData } from "@/models/layout";
import { Heading } from "@/components/core/heading";
import Text from "@/components/core/text";
import { Button } from "@/components/core/button";

interface Props {
    data: LayoutData;
}

export default function Popup({ data }: Props) {
    const pathname = usePathname();
    const lenis = useLenis();
    
    const [isVisible, setIsVisible] = useState(false);
    const [isMaximized, setIsMaximized] = useState(false);
    const [emailConfig, setEmailConfig] = useState<{
        [key: string]: any;
    } | null>(null);
    
    const isDev = process.env.NODE_ENV === "development";

    const exclude_urls = data.popup.exclude_pages.map(page => page.url);

    const show = useCallback(() => {
        setIsVisible(true);
        setIsMaximized(true);
    }, []);

    const maximize = useCallback(() => {
        document.body.style.overflow = "hidden";
        setIsMaximized(true);

        if (lenis) {
            lenis.stop();
        }
    }, [lenis]);

    const hide = useCallback(() => {
        document.body.style.overflow = "auto";
        setIsVisible(false);

        if (!lenis) return;

        lenis.start();
    }, [lenis]);

    const accept = useCallback(() => {
        localStorage.setItem("popupAccepted", "true");

        localStorage.removeItem("popupDismissedTime");

        hide();
    }, [hide]);

    useEffect(() => {
        setEmailConfig({
            formId: parseInt(data.popup.email_form_id),
            template: "clare",
            submitText: data.popup.opt_in,
            buttonBackground: "#F7FF07",
            buttonColor: "#3E3E3D",
            namePlaceholder: "Name",
            emailPlaceholder: "Email",
        });
    }, [data.popup]);

    useEffect(() => {
        const popupAccepted = localStorage.getItem("popupAccepted");

        if (popupAccepted === "true" || exclude_urls.includes(pathname)) {
            return;
        }

        const popupDismissedTime = localStorage.getItem("popupDismissedTime");

        if (popupDismissedTime) {
            const thirtyDaysInMs =
                (data.popup.display_frequency ?? 30) * 24 * 60 * 60 * 1000;
            if (
                new Date().getTime() - parseInt(popupDismissedTime, 10) <
                thirtyDaysInMs
            ) {
                return;
            }
        }

        const handleScroll = (e: any) => {
            if (e.progress > 0.07) {
                show();
                lenis?.off('scroll', handleScroll);
            }
        };

        if (lenis) {
            lenis.on("scroll", handleScroll);
        } else {
            const fallbackTimer = setTimeout(() => show(), 3000);
            return () => clearTimeout(fallbackTimer);
        }

        const handleConvertKitSubmit = (e: any) => {
            accept();
        };

        document.addEventListener(
            "ckjs:submission:complete",
            handleConvertKitSubmit,
        );

        return () => {
            if (lenis) {
                lenis.off("scroll", handleScroll);
            }

            document.removeEventListener(
                "ckjs:submission:complete",
                handleConvertKitSubmit,
            );
        };
    }, [lenis, accept, show, data.popup.display_frequency, exclude_urls, pathname]);

    

    const dismiss = (e: React.MouseEvent) => {
        e.preventDefault();

        localStorage.setItem(
            "popupDismissedTime",
            new Date().getTime().toString(),
        );

        hide();
    };

    if (!isVisible) return null;

    if (!isMaximized) return (
        <>
            <div className="fixed top-0 left-0 w-dvw h-dvh z-50 flex items-end justify-center p-4 touch-none pointer-events-none">
                <div className="bg-tertiary rounded-lg w-full max-w-lg p-4 shadow-lg flex flex-col md:flex-row items-center justify-center md:justify-between space-y-4 md:space-y-0 pointer-events-auto">
                    <Heading
                        as="h1"
                        size="3xl"
                        leading="tight"
                        transform="uppercase"
                        content={`${data.popup.headline}`}
                        className="text-center md:text-left"
                    ></Heading>
                    <Button
                        theme_color="tertiary"
                        size="xs"
                        label="Learn More"
                        onClick={maximize}
                    />
                </div>
            </div>
        </>
    );

    return (
        <>
            <div className="fixed top-0 left-0 w-dvw h-dvh z-50 flex items-center justify-center p-4">
                <div className="bg-white rounded-lg w-full max-w-2xl p-4 shadow-lg">
                    <div className="block md:flex w-full gap-4">
                        {data.popup.image && (
                            <CoreImage
                                priority={true}
                                className="rounded-xl w-full hidden md:block md:w-2/5"
                                src={getAssetUrl(data.popup.image)}
                                width={data.popup.image.width}
                                height={data.popup.image.height}
                                alt={data.popup.image.alt ?? ""}
                                style={{
                                    objectFit: "cover",
                                    objectPosition: data.popup.image.focus_css,
                                }}
                            />
                        )}
                        <div className="space-y-4 w-full md:w-3/5">
                            <div>
                                <Heading
                                    as="h1"
                                    size="5xl"
                                    leading="tight"
                                    transform="uppercase"
                                    content={data.popup.headline}
                                    className="text-center md:text-left text-4xl"
                                ></Heading>
                                <Text
                                    size="lg"
                                    leading="normal"
                                    className="text-center md:text-left space-y-4"
                                    content={data.popup.content}
                                />
                            </div>
                            <div className="text-center">
                                {emailConfig && (
                                    <>
                                        {true && (
                                            <ConvertKitForm {...emailConfig} />
                                        )}
                                    </>
                                )}
                                <button className="italic" onClick={dismiss}>
                                    {data.popup.opt_out}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
